import React, { useState, useCallback, useMemo } from 'react'
import store from 'Shared/store'
import { Provider } from 'react-redux'
import './Analytics.scoped.scss'
import Filters from './shared/Filters'
import Tabs from 'Shared/components/Tabs/Tabs'
import DateRangeSelect from './shared/DateRangeSelect'
import Button from 'Shared/components/Button'
import { format, parse, formatISO, parseISO, addDays } from 'date-fns'
import AnalyticsProvider from 'AnalyticsDashboard/components/AnalyticsProvider'
import AnalyticsQuery from 'AnalyticsDashboard/components/AnalyticsQuery'
import SimpleAnalyticsView from './SimpleAnalyticsView'
import NewAnalytics from './NewAnalytics'

const METRICS = {
  calls: [
    { id: 'm1', factTable: 'calls', column: '*', operation: 'count', label: 'Calls' },
    { id: 'm2', factTable: 'calls', column: 'spoken_to', operation: 'count_true', label: 'Spoken To' },
    { id: 'm3', factTable: 'calls', column: 'outcome', operation: 'count_where', value: 'answer', label: 'Answered' },
  ],
  text_messages: [
    { id: 'm1', factTable: 'text_messages', column: 'direction', operation: 'count_where', value: '1', label: 'Texted' },
    { id: 'm2', factTable: 'text_messages', customMetric: 'count_sms_outbound', label: 'SMS Outbound' },
    { id: 'm3', factTable: 'text_messages', customMetric: 'count_rcs_outbound', label: 'RCS Outbound' },
    { id: 'm4', factTable: 'text_messages', column: 'direction', operation: 'count_where', value: '0', label: 'Replies' },
    { id: 'm5', factTable: 'text_messages', column: 'clicked_at', operation: 'count_non_null', label: 'Clicked' },
  ],
  emails: [
    { id: 'm1', factTable: 'emails', column: '*', operation: 'count', label: 'Total' },
    { id: 'm2', factTable: 'emails', column: 'opened_at', operation: 'count_non_null', label: 'Opened' },
    { id: 'm3', factTable: 'emails', column: 'clicked_at', operation: 'count_non_null', label: 'Clicked' },
  ],
}

function SimpleAnalyticsAdapter({ data, query }) {
  const series = (query.metrics || []).map(m => ({ key: m.id, label: m.label || m.id }))
  const sorted = _.sortBy(data, 'time')
  return <SimpleAnalyticsView chartData={{ title: '', data: sorted, series }} />
}

const Analytics = () => {
  if ((new URLSearchParams(window.location.search)).get('newView') === '1') {
    return <NewAnalytics />
  }

  return (
    <Provider store={store}>
      <AnalyticsInner />
    </Provider>
  )
}

function buildFilterValues(dateRange, rules) {
  const fv = {}
  if (dateRange.from || dateRange.to) {
    fv.time = {
      from: dateRange.from ? formatISO(parse(dateRange.from, 'yyyy-MM-dd', new Date())) : '',
      to: dateRange.to ? formatISO(addDays(parse(dateRange.to, 'yyyy-MM-dd', new Date()), 1)) : ''
    }
  }
  if (rules.length > 0) {
    fv.rules_filter = rules
  }
  return fv
}

function AnalyticsInner() {
  const [screen, setScreen] = useState('calls')
  const dateRange = useSel((s) => s.analytics.dateRange)
  const rules = useSel(s => _.values(s.rules.entities))

  const [appliedFilters, setAppliedFilters] = useState(() => buildFilterValues(dateRange, rules))

  const handleApply = useCallback(() => {
    setAppliedFilters(buildFilterValues(dateRange, rules))
  }, [dateRange, rules])

  const tabs = [
    { text: 'Calling', key: 'calls', current: screen === 'calls' },
    { text: 'Texting', key: 'text_messages', current: screen === 'text_messages' },
    { text: 'Emails', key: 'emails', current: screen === 'emails' },
  ]

  const metrics = METRICS[screen] || []

  return <>
    <div className="sectionn tabs-and-filters">
      <Tabs tabs={tabs} onChange={setScreen} />
      <div>
        <DateRangeSelect />
      </div>
    </div>
    <Filters />
    <div className='margin-bottom'>
      <Button primary onClick={handleApply}>Update stats</Button>
    </div>
    <AnalyticsProvider
      key={`${screen}-${JSON.stringify(appliedFilters)}`}
      uiEnabled={false}
      filterValues={appliedFilters}
    >
      <AnalyticsQuery
        metrics={metrics}
        dimensions={[{ id: 'time', timeBucket: 'day' }]}
        chartComponent={SimpleAnalyticsAdapter}
        controlOptions={{ showChartType: false, showTimeBucket: false }}
      />
    </AnalyticsProvider>
  </>
}

export default Analytics
